/**
 * Which kind of order a row is: a meal supported for others, or a tiffin
 * ordered for yourself.
 *
 * Word, icon and colour together — the same icons Home puts on Support a Meal
 * and Order a Tiffin — because a colour alone says nothing to a screen reader
 * or to someone who cannot tell green from blue. Blue is the admin portal's
 * colour for a tiffin order too, so both sides of the product agree.
 */

import { memo } from 'react';
import { StyleSheet, View } from 'react-native';

import type { OrderType } from '@mfa/domain';
import { colors, radius, space } from '../../theme';
import { Glyph } from '../common/Icon';
import { Text } from '../common/Text';
import { useT } from '../../core/i18n/LanguageProvider';

export const ORDER_TYPE_LOOK: Record<
  OrderType,
  { label: string; glyph: string; accent: string; tint: string; ink: string }
> = {
  DONATION: {
    label: 'Meal Supported',
    glyph: 'choose',
    accent: colors.bg.brand,
    tint: colors.bg.brandSubtle,
    ink: colors.text.brand,
  },
  TIFFIN: {
    label: 'Tiffin',
    glyph: 'tiffin',
    accent: colors.status.info.solid,
    tint: colors.status.info.bg,
    ink: colors.status.info.fg,
  },
};

export const OrderTypeTag = memo(function OrderTypeTag({ type }: { type: OrderType }) {
  const t = useT();
  const look = ORDER_TYPE_LOOK[type];
  return (
    <View style={[styles.tag, { backgroundColor: look.tint }]} testID={`order-type-${type}`}>
      <Glyph name={look.glyph} size={12} />
      <Text variant="caption" style={[styles.label, { color: look.ink }]}>
        {t(look.label)}
      </Text>
    </View>
  );
});

const styles = StyleSheet.create({
  tag: {
    flexDirection: 'row',
    alignItems: 'center',
    alignSelf: 'flex-start',
    gap: space.xxs,
    paddingHorizontal: space.sm,
    paddingVertical: space.xxs,
    borderRadius: radius.full,
  },
  label: {
    fontWeight: '600',
  },
});
